/**
 * Which language this person reads, resolved once.
 *
 * The value is read from the DOM rather than recomputed, and that is deliberate — it is the
 * same discipline `brand/active.ts` applies to `data-brand` and `useTheme` applies to
 * `data-theme`. `boot.js` has already resolved it before the first paint, from the stored
 * choice, the served document's own `lang` (which nginx picked per brand and per
 * `Accept-Language`), or the browser. Reading it back here means one resolution rather than
 * two that can disagree, and no frame of the other language (PRD-27 D-10, LOC-004).
 *
 * `users.locale` on the account is the real source of truth (LOC-003). localStorage is its
 * pre-paint mirror and nothing else: it exists so `boot.js`, which cannot await a session,
 * has something to stamp. When the session arrives and disagrees — because the person
 * switched language on another device — the account wins (`useAccountLocale`).
 */

export type Locale = 'en' | 'pt-BR'

/** `en` first: it is the source catalogue, the type, and the fallback. */
export const LOCALES: readonly Locale[] = ['en', 'pt-BR']

export const DEFAULT_LOCALE: Locale = 'en'

export const LOCALE_STORAGE_KEY = 'ralph.locale'

export function isLocale(value: unknown): value is Locale {
  return value === 'en' || value === 'pt-BR'
}

/**
 * Any Portuguese is Brazilian Portuguese here (LOC-001).
 *
 * A prefix match rather than an exact one, because a browser sends `pt`, `pt-BR`, `pt-PT`
 * and `pt-br` and a person who set their system to Portugal is far better served by
 * Brazilian Portuguese than by English. If `pt-PT` ever ships this becomes a real regional
 * match and this comment becomes the reason it was not one.
 */
export function normaliseLocale(value: unknown): Locale | null {
  if (typeof value !== 'string') return null
  const lower = value.trim().toLowerCase()
  if (!lower) return null
  if (lower.startsWith('pt')) return 'pt-BR'
  if (lower.startsWith('en')) return 'en'
  return null
}

/**
 * The precedence chain of LOC-002, as a pure function so `boot.js` can mirror it and
 * `locale.spec.ts` can prove every branch without a DOM.
 *
 * Stored choice wins over everything: it is the one signal that is an actual decision by a
 * person rather than an inference about them.
 *
 * The browser comes BEFORE the brand's default, and that ordering was corrected rather than
 * designed: the first draft had the brand default win, and a live walkthrough caught it
 * disagreeing with the nginx negotiation built for the same question one layer up. A brand
 * default is a guess about a POPULATION; a browser language is a statement by the PERSON in
 * front of you, and a Neoenergia employee who set their browser to English should get English.
 * The brand default is the floor — what a deployment falls back to when the browser asks for
 * a language this bundle does not ship — which is exactly the role it plays in `try_files`.
 *
 * `served` is NOT `document.documentElement.lang`, and the distinction is load-bearing.
 * Every build carries some `lang`, so trusting it would mean the default build's `en` beat
 * every brand default forever. It is `data-served-locale`, which only the per-locale
 * document variants carry — so it means "nginx deliberately chose this locale for this
 * request", and its absence correctly means "nobody has chosen yet" (D-12).
 */
export function resolveLocale(input: {
  stored?: unknown
  served?: unknown
  brandDefault?: unknown
  browser?: readonly string[]
}): Locale {
  const stored = normaliseLocale(input.stored)
  if (stored) return stored

  const served = normaliseLocale(input.served)
  if (served) return served

  for (const candidate of input.browser ?? []) {
    const matched = normaliseLocale(candidate)
    if (matched) return matched
  }

  const brand = normaliseLocale(input.brandDefault)
  if (brand) return brand

  return DEFAULT_LOCALE
}

/**
 * What `boot.js` stamped. `document.documentElement.lang` is not a mirror of the locale —
 * it *is* the locale, and it is also what a screen reader, a spellchecker and a hyphenation
 * engine read (LOC-006), so there is no second attribute to keep in step with it.
 */
export function detectLocale(): Locale {
  if (typeof document === 'undefined') return DEFAULT_LOCALE
  const stamped = normaliseLocale(document.documentElement.lang)
  if (stamped) return stamped
  // `boot.js` did not run — a test, a storybook mount, or a browser that blocked it.
  // Resolve here rather than defaulting, so those environments behave like the real one.
  return resolveLocale({
    stored: readStoredLocale(),
    served: document.documentElement.dataset.servedLocale,
    browser: typeof navigator === 'undefined' ? [] : (navigator.languages ?? [navigator.language])
  })
}

export function readStoredLocale(): Locale | null {
  try {
    return normaliseLocale(localStorage.getItem(LOCALE_STORAGE_KEY))
  } catch {
    // A locked-down browser profile throws rather than returning null. The account's
    // `users.locale` still governs the next sign-in; this tab simply has no memory.
    return null
  }
}

export function persistLocale(locale: Locale): void {
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, locale)
  } catch {
    // Best effort. The in-memory choice still applies for this tab, and `PATCH /api/me`
    // has already carried it to the account, which is the durable copy.
  }
}
